import { createAsyncThunk } from "@reduxjs/toolkit";
import ChatSocketServices from "../../Services/ChatSocketServices";
import Services from "../../Services/Services";

export const jwtToken = createAsyncThunk(
    "chatSocket/jwtToken",
    async (OrgUserToken, { rejectWithValue }) => {
        try {
            const response = await ChatSocketServices.jwtToken(OrgUserToken);
            return response?.data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const getUserData = createAsyncThunk(
    "chatSocket/getUserData",
    async (jwtToken, { rejectWithValue }) => {
        try {
            const response = await ChatSocketServices.getUserData(jwtToken);
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const createChat = createAsyncThunk(
    "chatSocket/createChat",
    async ({ jwtToken, access, organizationId, botName = null }, { rejectWithValue }) => {
        try {
            const response = await ChatSocketServices.createChat({
                jwtToken,
                access,
                organizationId,
                botName,
            });
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const getMessagesHistory = createAsyncThunk(
    "chatSocket/getMessagesHistory",
    async ({ jwtToken, userChatId }, { rejectWithValue }) => {
        try {
            const response = await ChatSocketServices.getMessagesHistory({
                jwtToken,
                userChatId,
            });
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

// Page context must never be concatenated into `content` — stored messages
// and the agent dashboard would then leak [PAGE_CONTEXT] tags. Instead it is
// forwarded as a sibling metadata field that the backend routes to the AI.
function selectPageContext(state) {
    const pageContext = state.chatServicesData?.pageContext;
    if (!pageContext || !pageContext.url) return null;
    return pageContext;
}

export const sendMessage = createAsyncThunk(
    "chatSocket/sendMessage",
    async ({ jwtToken, userChatId, content, botName }, { rejectWithValue, getState }) => {
        try {
            const response = await ChatSocketServices.sendMessage({
                jwtToken,
                userChatId,
                content,
                botName,
                pageContext: selectPageContext(getState()),
            });
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const sendMessageForBot = createAsyncThunk(
    "chatSocket/sendMessageForBot",
    async ({ jwtToken, userChatId, content, botName }, { rejectWithValue, getState }) => {
        try {
            const response = await ChatSocketServices.sendMessageForBot({
                jwtToken,
                userChatId,
                content,
                botName,
                pageContext: selectPageContext(getState()),
            });
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response.data);
        }
    }
);

export const fetchMessages = createAsyncThunk(
    "chatSocket/fetchMessages",
    async ({ chatId, page = 0, size = 30 }, { rejectWithValue }) => {
        try {
            const response = await Services.fetchMessages(chatId, { page, size });
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const fetchAllMessages = createAsyncThunk(
    "chatSocket/fetchAllMessages",
    async (chatId, { rejectWithValue, getState }) => {
        try {
            const state = getState();
            const currentUserId = state.auth?.user?.id;
            const currentChatId = state.auth?.chatId;
            
            const response = await Services.fetchAllMessages(chatId);
            
            // Validate that messages belong to current user's chat
            if (response.data && response.data.messages) {
                const messages = response.data.messages;
                
                // Filter messages to ensure they belong to current user's chat
                // Use the chatId parameter (the chat we're fetching for) as primary check
                const filteredMessages = messages.filter(msg => {
                    const msgChatId = msg.chat?.id || msg.chatId;
                    const msgSenderId = msg.sender?.id || msg.senderId;
                    
                    // Primary check: message belongs to the chat we're fetching
                    const belongsToRequestedChat = msgChatId === chatId || 
                                                   msgChatId?.toString() === chatId?.toString();
                    
                    // Secondary check: message is from current user (in case chatId doesn't match)
                    const isFromCurrentUser = msgSenderId === currentUserId?.toString() ||
                                            msgSenderId?.toString() === currentUserId?.toString();
                    
                    // Tertiary check: message belongs to current chat from state (if available)
                    const belongsToCurrentChat = currentChatId && (
                        msgChatId === currentChatId || 
                        msgChatId?.toString() === currentChatId?.toString()
                    );
                    
                    // Include message if it matches any of the criteria
                    return belongsToRequestedChat || isFromCurrentUser || belongsToCurrentChat;
                });
                response.data.messages = filteredMessages;
            }
            
            return response.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

